/**
 * API client for scheduler endpoints
 */

import { withTurnstileToken } from './turnstile.js'

const API_BASE = '/api/scheduler'

// Request cache for deduplication and short-term caching
const requestCache = {
  // In-flight requests: key -> Promise
  inFlight: new Map(),
  // Cached responses: key -> { data, timestamp }
  cache: new Map(),
  // Cache TTL in milliseconds (10 seconds for GET requests - increased for better deduplication)
  cacheTTL: 10000
}

/**
 * Generate a cache key from endpoint and options
 */
function getCacheKey(endpoint, options = {}) {
  const method = (options.method || 'GET').toUpperCase()
  const body = options.body ? (typeof options.body === 'string' ? options.body : JSON.stringify(options.body)) : ''
  return `${method}:${endpoint}:${body}`
}

/**
 * Check if a cached response is still valid
 */
function isCacheValid(cached) {
  if (!cached) return false
  return Date.now() - cached.timestamp < requestCache.cacheTTL
}

/**
 * Invalidate cache entries matching a pattern
 * Pattern should be an endpoint path (e.g., '/invoices' or '/invoices/27')
 */
function invalidateCache(pattern) {
  if (pattern) {
    // Normalize pattern to match cache key format (cache keys are like "GET:/invoices:")
    const normalizedPattern = pattern.startsWith('/') ? pattern : `/${pattern}`
    
    // Invalidate specific endpoint pattern
    for (const key of requestCache.cache.keys()) {
      // Cache keys are formatted as "METHOD:ENDPOINT:BODY"
      // Check if the endpoint part matches (strip query params for comparison)
      const parts = key.split(':')
      if (parts.length >= 2) {
        const endpoint = parts[1]
        // Remove query string for comparison (e.g., "/reservations/calendar?start=..." -> "/reservations/calendar")
        const endpointWithoutQuery = endpoint.split('?')[0]
        if (endpointWithoutQuery === normalizedPattern || endpointWithoutQuery.startsWith(normalizedPattern + '/')) {
          requestCache.cache.delete(key)
          console.log(`[API Cache] Invalidated cache entry: ${key}`)
        }
      }
    }
    for (const key of requestCache.inFlight.keys()) {
      const parts = key.split(':')
      if (parts.length >= 2) {
        const endpoint = parts[1]
        // Remove query string for comparison
        const endpointWithoutQuery = endpoint.split('?')[0]
        if (endpointWithoutQuery === normalizedPattern || endpointWithoutQuery.startsWith(normalizedPattern + '/')) {
          requestCache.inFlight.delete(key)
        }
      }
    }
  } else {
    // Invalidate all cache
    requestCache.cache.clear()
    requestCache.inFlight.clear()
  }
}

/**
 * Check if a request method is a mutation (should invalidate cache)
 */
function isMutation(method) {
  const upperMethod = (method || 'GET').toUpperCase()
  return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(upperMethod)
}

class APIError extends Error {
  constructor(message, { status, data, sessionExpired = false } = {}) {
    super(message)
    this.name = 'APIError'
    this.status = status
    this.data = data
    this.sessionExpired = sessionExpired
  }
}

async function request(endpoint, options = {}) {
  const method = (options.method || 'GET').toUpperCase()
  const isGetRequest = method === 'GET'
  const cacheKey = getCacheKey(endpoint, options)

  // Check for in-flight request (deduplication)
  if (requestCache.inFlight.has(cacheKey)) {
    console.log(`[API Cache] Deduplicating request: ${method} ${endpoint}`)
    return requestCache.inFlight.get(cacheKey)
  }

  // Check cache for GET requests
  if (isGetRequest) {
    const cached = requestCache.cache.get(cacheKey)
    if (isCacheValid(cached)) {
      const age = Date.now() - cached.timestamp
      console.log(`[API Cache] Using cached response for ${endpoint} (age: ${age}ms)`)
      return Promise.resolve(cached.data)
    }
  }

  // Create the request promise
  const requestPromise = (async () => {
    const url = `${API_BASE}${endpoint}`
    const config = {
      ...options,
      // Explicit so auth cookies are always sent (Safari / cross-subdomain edge cases)
      credentials: options.credentials || 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    }

    if (options.body && typeof options.body === 'object') {
      config.body = JSON.stringify(options.body)
    }

    try {
      const response = await fetch(url, config)
    
    // Check if response has content before parsing JSON
    const contentType = response.headers.get('content-type')
    if (!contentType || !contentType.includes('application/json')) {
      // If it's not JSON, check if it's an error response
      if (!response.ok) {
        const text = await response.text()
        throw new Error(text || `HTTP ${response.status}: ${response.statusText}`)
      }
      // Empty response or non-JSON response
      throw new Error('Invalid response from server. Make sure the API server is running.')
    }
    
    const text = await response.text()
    if (!text) {
      throw new Error('Empty response from server. Make sure the API server is running.')
    }
    
    let data
    try {
      data = JSON.parse(text)
    } catch (parseError) {
      console.error('Failed to parse JSON response:', text)
      throw new Error('Invalid JSON response from server')
    }

    // Handle session expiration (401 Unauthorized)
    if (response.status === 401) {
      // Clear auth and trigger session expiration event
      const { clearAuth } = await import('./auth.js')
      clearAuth()
      
      // Dispatch custom event for auth context to handle
      window.dispatchEvent(new CustomEvent('session-expired'))
      
      throw new APIError(data?.error || 'Session expired. Please log in again.', { 
        status: response.status, 
        data,
        sessionExpired: true 
      })
    }

      if (!response.ok) {
        throw new APIError(data?.error || 'Request failed', { status: response.status, data })
      }

      // Cache successful GET requests
      if (isGetRequest) {
        requestCache.cache.set(cacheKey, {
          data,
          timestamp: Date.now()
        })
        console.log(`[API Cache] Cached response for ${endpoint}`)
      }

      // Invalidate related cache entries on mutations
      if (isMutation(method)) {
        // Extract resource from endpoint (e.g., '/invoices' from '/invoices/27/mark-paid')
        // This will invalidate all related endpoints for that resource
        const parts = endpoint.split('/').filter(p => p)
        if (parts.length > 0) {
          const resource = `/${parts[0]}`
          invalidateCache(resource)
          // Also invalidate the specific endpoint if it's an ID-based operation
          if (parts.length > 1 && !isNaN(parseInt(parts[1]))) {
            invalidateCache(`${resource}/${parts[1]}`)
          }
        }
      }

      return data
    } catch (error) {
      // Provide more helpful error messages
      if (error.message.includes('ECONNREFUSED') || error.message.includes('Failed to fetch')) {
        throw new Error('Cannot connect to API server. Please make sure Wrangler is running (npm run wrangler:dev)')
      }

      // Avoid noisy console errors for expected 4xx validation failures
      if (!(error instanceof APIError && error.status >= 400 && error.status < 500)) {
        console.error('API request error:', error)
      }
      throw error
    } finally {
      // Remove from in-flight requests
      requestCache.inFlight.delete(cacheKey)
    }
  })()

  // Store in-flight request for deduplication
  requestCache.inFlight.set(cacheKey, requestPromise)

  return requestPromise
}

// Export cache utilities for manual invalidation if needed
export const apiCache = {
  invalidate: invalidateCache,
  clear: () => {
    requestCache.cache.clear()
    requestCache.inFlight.clear()
  }
}

// Auth endpoints
export const authAPI = {
  login: (email, password, turnstileToken) => request('/auth/login', {
    method: 'POST',
    body: withTurnstileToken({ email, password }, turnstileToken),
  }),
  logout: () => request('/auth/logout', {
    method: 'POST'
  }),
  me: () => request('/auth/me', {
    method: 'GET'
  }),
  forgotPassword: (email) => request('/auth/forgot-password', {
    method: 'POST',
    body: { email }
  }),
  resetPassword: (token, password) => request('/auth/reset-password', {
    method: 'POST',
    body: { token, password }
  })
}

// Rooms endpoints
export const roomsAPI = {
  list: () => request('/rooms'),
  get: (id) => request(`/rooms/${id}`),
  create: (data) => request('/rooms', {
    method: 'POST',
    body: data
  }),
  update: (id, data) => request(`/rooms/${id}`, {
    method: 'PUT',
    body: data
  }),
  delete: (id) => request(`/rooms/${id}`, {
    method: 'DELETE'
  }),
  toggleActive: (id, active) => request(`/rooms/${id}/active`, {
    method: 'PATCH',
    body: { active }
  })
}

// Users endpoints
export const usersAPI = {
  list: () => request('/users'),
  get: (id) => request(`/users/${id}`),
  create: (data) => request('/users', {
    method: 'POST',
    body: data
  }),
  invite: (data) => request('/users/invite', {
    method: 'POST',
    body: data
  }),
  update: (id, data) => request(`/users/${id}`, {
    method: 'PUT',
    body: data
  }),
  updateMe: (data) => request('/users/me', {
    method: 'PUT',
    body: data
  }),
  delete: (id) => request(`/users/${id}`, {
    method: 'DELETE'
  }),
  toggleActive: (id, active) => request(`/users/${id}/active`, {
    method: 'PATCH',
    body: { active }
  })
}

// Groups endpoints
export const groupsAPI = {
  list: ({ includeInactive = false } = {}) =>
    request(includeInactive ? '/groups?include_inactive=1' : '/groups'),
  get: (id) => request(`/groups/${id}`),
  create: (data) => request('/groups', {
    method: 'POST',
    body: data
  }),
  update: (id, data) => request(`/groups/${id}`, {
    method: 'PUT',
    body: data
  }),
  delete: (id) => request(`/groups/${id}`, {
    method: 'DELETE'
  })
}

// Scheduler settings endpoints
export const settingsAPI = {
  get: () => request('/settings'),
  update: (data) => request('/settings', {
    method: 'PUT',
    body: data
  })
}

// Reservations endpoints
export const reservationsAPI = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString()
    return request(`/reservations${query ? `?${query}` : ''}`)
  },
  get: (id) => request(`/reservations/${id}`),
  create: (data) => request('/reservations', {
    method: 'POST',
    body: data
  }),
  update: (id, data) => request(`/reservations/${id}`, {
    method: 'PUT',
    body: data
  }),
  delete: (id) => request(`/reservations/${id}`, {
    method: 'DELETE'
  }),
  calendar: (start, end) => request(`/reservations/calendar?start=${start}&end=${end}`),
  checkAvailability: (roomId, startTime, endTime, excludeReservationId) => {
    const params = new URLSearchParams({
      room_id: roomId,
      start_time: startTime,
      end_time: endTime
    })
    if (excludeReservationId) {
      params.append('exclude_reservation_id', excludeReservationId)
    }
    return request(`/reservations/availability?${params.toString()}`)
  }
}

// Invoices endpoints
export const invoicesAPI = {
  list: () => request('/invoices'),
  get: (id) => request(`/invoices/${id}`),
  create: (data) => request('/invoices', {
    method: 'POST',
    body: data
  }),
  verify: (id) => request(`/invoices/${id}/verify`, {
    method: 'POST'
  }),
  delete: (id) => request(`/invoices/${id}`, {
    method: 'DELETE'
  }),
  createPaymentSession: (id) => request(`/invoices/${id}/payment`, {
    method: 'POST'
  }),
  markPaid: (id) => request(`/invoices/${id}/mark-paid`, {
    method: 'POST'
  }),
  cancel: (id, { justification = '', notifyUser = false } = {}) => request(`/invoices/${id}/cancel`, {
    method: 'POST',
    body: { justification, notify_user: notifyUser }
  }),
  syncPayment: (id) => request(`/invoices/${id}/sync-payment`, {
    method: 'POST'
  }),
  testWebhook: (invoiceId, eventType) => request('/stripe/test-webhook', {
    method: 'POST',
    body: { invoice_id: invoiceId, event_type: eventType }
  })
}

// Stripe endpoints
export const stripeAPI = {
  getStatus: () => request('/stripe/status'),
  getConfig: () => request('/stripe/config')
}

// Reports endpoints
export const reportsAPI = {
  users: (params = {}) => {
    const query = new URLSearchParams(params).toString()
    return request(`/reports/users${query ? `?${query}` : ''}`)
  },
  audit: (params = {}) => {
    const query = new URLSearchParams(params).toString()
    return request(`/reports/audit${query ? `?${query}` : ''}`)
  },
  rooms: (params = {}) => {
    const query = new URLSearchParams(params).toString()
    return request(`/reports/rooms${query ? `?${query}` : ''}`)
  },
  reservations: (params = {}) => {
    const query = new URLSearchParams(params).toString()
    return request(`/reports/reservations${query ? `?${query}` : ''}`)
  },
  billing: (params = {}) => {
    const query = new URLSearchParams(params).toString()
    return request(`/reports/billing${query ? `?${query}` : ''}`)
  },
  dashboard: () => request('/reports/dashboard')
}
